import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Loader2, Search, ChevronDown, UploadCloud, CheckCircle2, Sparkles, AlertTriangle, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

const money = (v) => `£${(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => (v === null || v === undefined || v === '') ? '—' : `${(v * 100).toFixed(1)}%`;

const ROW_STATUS_STYLE = {
  live:           { label: 'Live',                     background: '#dcfce7', color: '#15803d', Icon: CheckCircle2 },
  pending_claim:  { label: 'New Claim — Pending',       background: '#f3e8ff', color: '#7e22ce', Icon: Sparkles },
  missing:        { label: 'Missing — Needs Review',    background: '#fee2e2', color: '#b91c1c', Icon: AlertTriangle },
};

function RowStatusBadge({ status, autoAdded, claimedMonth }) {
  const s = ROW_STATUS_STYLE[status] || ROW_STATUS_STYLE.missing;
  const Icon = s.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span
        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap"
        style={{ background: s.background, color: s.color }}
        data-testid={`ho-support-monthly-claimed-status-${status}`}
      >
        <Icon className="w-3 h-3" /> {s.label}
      </span>
      {autoAdded && (
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap bg-gray-100 text-gray-500"
          data-testid="ho-support-monthly-claimed-auto-added-tag"
          title="Not manually reviewed yet — added automatically because this upload showed real support"
        >
          Auto-added · {claimedMonth || '—'}
        </span>
      )}
    </span>
  );
}

/**
 * Monthly Claimed — 4th Head Office Support tab. Picks a monthly "30 - HO
 * Support" upload and shows every parsed account/supplier/group row
 * cross-referenced against Live Accounts / New Claims. Every row on this
 * upload IS real support already being received that month, so there's no
 * "apply to New Claims" step — anything not already Live gets auto-added to
 * Live Accounts by the backend at upload time (tagged "Auto-added"); this tab
 * just surfaces that plus the rare £0-this-month exceptions still worth a
 * manual look. Upload itself reuses the existing BI Reports upload
 * pill/modal (passed in via onOpenUpload) — no new uploader here.
 */
export default function HOSupportMonthlyClaimed({ onOpenUpload, refreshSignal, onDataChanged }) {
  const [uploads, setUploads] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState('');
  const [claimedRows, setClaimedRows] = useState([]);
  const [counts, setCounts] = useState({ live: 0, pending_claim: 0, missing: 0 });
  const [loadingUploads, setLoadingUploads] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [search, setSearch] = useState('');
  const [promotingKey, setPromotingKey] = useState(null);

  const loadUploads = useCallback(async () => {
    setLoadingUploads(true);
    try {
      const res = await api.get(`${API}/order-book-reports/ho-support/monthly-uploads`);
      const list = res.data?.uploads || [];
      setUploads(list);
      setSelectedUploadId((prev) => (prev && list.some((u) => u.id === prev)) ? prev : (list[0]?.id || ''));
    } catch {
      setUploads([]);
    } finally {
      setLoadingUploads(false);
    }
  }, []);

  useEffect(() => { loadUploads(); }, [loadUploads, refreshSignal]);

  const loadClaimed = useCallback(async () => {
    if (!selectedUploadId) { setClaimedRows([]); setCounts({ live: 0, pending_claim: 0, missing: 0 }); return; }
    setLoadingRows(true);
    try {
      const res = await api.get(`${API}/order-book-reports/ho-support/monthly-uploads/${selectedUploadId}/claimed`);
      setClaimedRows(res.data?.rows || []);
      setCounts(res.data?.counts || { live: 0, pending_claim: 0, missing: 0 });
    } catch {
      setClaimedRows([]);
      toast.error('Failed to load monthly claimed data');
    } finally {
      setLoadingRows(false);
    }
  }, [selectedUploadId]);

  useEffect(() => { loadClaimed(); }, [loadClaimed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return claimedRows;
    return claimedRows.filter((r) =>
      (r.account_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.supplier_code || '').toLowerCase().includes(q) ||
      (r.group || '').toLowerCase().includes(q)
    );
  }, [claimedRows, search]);

  const refreshAll = () => { loadClaimed(); onDataChanged?.(); };

  const promoteRow = async (r) => {
    const key = `${r.account_number}-${r.supplier_code}-${r.group}`;
    setPromotingKey(key);
    try {
      await api.post(`${API}/order-book-reports/ho-support/monthly-uploads/claimed/promote`, {
        account_number: r.account_number,
        customer_name: r.customer_name,
        supplier_code: r.supplier_code,
        group: r.group,
        claim_id: r.claim_id,
        support_pct: r.claim_support_pct ?? (r.net_sales ? r.support_amount / r.net_sales : null),
      });
      toast.success(`${r.account_number} promoted to Live Accounts`);
      refreshAll();
    } catch {
      toast.error('Failed to promote');
    } finally {
      setPromotingKey(null);
    }
  };

  const addMissingAsLive = async (r) => {
    const key = `${r.account_number}-${r.supplier_code}-${r.group}`;
    setPromotingKey(key);
    try {
      await api.post(`${API}/order-book-reports/ho-support/live-accounts/manual`, {
        account_number: r.account_number,
        customer_name: r.customer_name,
        supplier_code: r.supplier_code,
        group: r.group,
        support_pct: r.net_sales ? Math.round((r.support_amount / r.net_sales) * 10000) / 10000 : null,
        product_scope: null,
      });
      toast.success(`${r.account_number} added to Live Accounts`);
      refreshAll();
    } catch {
      toast.error('Failed to add live account');
    } finally {
      setPromotingKey(null);
    }
  };

  const selectedUpload = uploads.find((u) => u.id === selectedUploadId);

  return (
    <div>
      {/* Month selector + search + upload */}
      <div className="flex items-center justify-between gap-3 px-1 pb-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <select
              value={selectedUploadId}
              onChange={(e) => setSelectedUploadId(e.target.value)}
              disabled={loadingUploads || uploads.length === 0}
              data-testid="ho-support-monthly-claimed-upload-select"
              className="appearance-none bg-white text-gray-700 text-xs sm:text-sm font-semibold rounded-full pl-4 pr-8 py-1.5 border-2 cursor-pointer shadow-sm disabled:opacity-60"
              style={{ borderColor: enconOrange }}
            >
              {uploads.length === 0 && <option value="">No uploads yet</option>}
              {uploads.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.month || '—'} · {new Date(u.uploaded_at).toLocaleDateString('en-GB')} · {u.filename}
                </option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {selectedUpload && (
            <span className="text-xs text-gray-500" data-testid="ho-support-monthly-claimed-row-count">
              {selectedUpload.row_count} rows parsed
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onOpenUpload && (
            <button
              onClick={onOpenUpload}
              data-testid="ho-support-monthly-claimed-upload-btn"
              className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs sm:text-sm font-bold text-white transition-colors"
              style={{ backgroundColor: enconOrange }}
            >
              <UploadCloud className="w-3.5 h-3.5" /> Upload Monthly File
            </button>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search account, customer, supplier, group…"
              data-testid="ho-support-monthly-claimed-search-input"
              className="pl-8 pr-3 py-1.5 text-xs sm:text-sm rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 w-64"
            />
          </div>
        </div>
      </div>

      {/* Counts overview */}
      <div className="flex items-center gap-4 px-1 pb-3 text-sm text-gray-600 flex-wrap" data-testid="ho-support-monthly-claimed-overview">
        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> <span className="font-bold text-gray-900">{counts.live}</span> Live</span>
        <span className="text-gray-300">·</span>
        <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-purple-600" /> <span className="font-bold text-gray-900">{counts.pending_claim}</span> New Claim (Pending)</span>
        <span className="text-gray-300">·</span>
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-600" /> <span className="font-bold text-red-600">{counts.missing}</span> Missing — Needs Review</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ho-support-monthly-claimed-table">
            <thead>
              <tr style={{ backgroundColor: enconOrange }}>
                {['Account', 'Supplier', 'Group', 'Net Sales', 'Support £', 'Contr %', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-white uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(loadingUploads || loadingRows) && (
                <tr><td colSpan={8} className="text-center py-14">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: enconOrange }} />
                </td></tr>
              )}
              {!loadingUploads && !loadingRows && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-14 text-gray-400">
                  {uploads.length === 0 ? 'No monthly upload yet — upload this month\'s "30 - HO Support" file to get started.' : 'No rows match'}
                </td></tr>
              )}
              {!loadingUploads && !loadingRows && filtered.map((r) => {
                const key = `${r.account_number}-${r.supplier_code}-${r.group}`;
                const busy = promotingKey === key;
                return (
                  <tr key={key} className="border-t border-gray-100 hover:bg-orange-50/40 transition-colors" data-testid={`ho-support-monthly-claimed-row-${r.account_number}`}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono font-semibold text-gray-800">{r.account_number}</span>
                      {r.customer_name && <span className="text-gray-500 font-normal ml-2">— {r.customer_name}</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-700">{r.supplier_code}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{r.group}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{money(r.net_sales)}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{money(r.support_amount)}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{pct(r.contr_pct_after)}</td>
                    <td className="px-4 py-3">
                      <RowStatusBadge status={r.status} autoAdded={r.live_source === 'auto_added_monthly_upload'} claimedMonth={r.live_claimed_month} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.status === 'pending_claim' && (
                        <button
                          onClick={() => promoteRow(r)}
                          disabled={busy}
                          data-testid={`ho-support-monthly-claimed-promote-${r.account_number}`}
                          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-white disabled:opacity-50"
                          style={{ backgroundColor: '#7e22ce' }}
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Promote
                        </button>
                      )}
                      {r.status === 'missing' && (
                        <button
                          onClick={() => addMissingAsLive(r)}
                          disabled={busy}
                          data-testid={`ho-support-monthly-claimed-add-live-${r.account_number}`}
                          className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full text-white disabled:opacity-50"
                          style={{ backgroundColor: '#15803d' }}
                          title="Add straight to Live Accounts"
                        >
                          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3" />} Live
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
